import React, { useEffect, useRef } from 'react'
import * as echarts from 'echarts'
import type { EChartsOption } from 'echarts'
import { CLIMATE_ECHARTS_THEME, climateThemeObject, CLIMATE_ARIA_DECAL } from './theme'

// Register the climate theme once at module load. Because the apps bundle this
// library from source, `echarts` resolves from climate_components/node_modules,
// so there is one echarts instance and one registration.
echarts.registerTheme(CLIMATE_ECHARTS_THEME, climateThemeObject)

export interface EChartProps {
  option: EChartsOption
  height?: number | string
  className?: string
  'data-testid'?: string
  // notMerge controls whether setOption replaces or merges; default merge
  // (false) so incremental data updates animate.
  notMerge?: boolean
  // Decal textures are ON by default (the reference look: bold blues + a light
  // hatch). Pass decals={false} to disable for a specific chart.
  decals?: boolean
  // Called once with the ECharts instance after init (and undefined on dispose),
  // so a caller can wire pixel<->data conversion or zrender pointer events.
  onInit?: (chart: echarts.ECharts | undefined) => void
}

// Thin, dependency-light React wrapper over ECharts. The community
// echarts-for-react wrapper is unmaintained (3+ years, old echarts), so this
// owns exactly what we need: init with the climate theme, keep the option in
// sync, resize with the container, and dispose on unmount. ~40 lines of glue,
// not a framework.
const EChart = ({ option, height = 400, className, notMerge = false, decals = true, onInit, ...rest }: EChartProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<echarts.ECharts | undefined>(undefined)
  const onInitRef = useRef(onInit)
  onInitRef.current = onInit

  useEffect(() => {
    if (!containerRef.current) return
    const chart = echarts.init(containerRef.current, CLIMATE_ECHARTS_THEME, { renderer: 'svg' })
    chartRef.current = chart
    onInitRef.current?.(chart)
    const observer = new ResizeObserver(() => chart.resize())
    observer.observe(containerRef.current)
    return () => {
      observer.disconnect()
      chart.dispose()
      chartRef.current = undefined
      onInitRef.current?.(undefined)
    }
  }, [])

  useEffect(() => {
    // Series are distinguished by the Tellus blue ramp (color) by default.
    // Texture decals are opt-in (decals prop) for the rare >5-series case.
    // Disable the entry/update animation (the initial-render sweep is distracting);
    // hover/emphasis states are unaffected since they aren't driven by this flag.
    const base = 'animation' in option ? option : { ...option, animation: false }
    const next = decals && !('aria' in base) ? { ...base, aria: CLIMATE_ARIA_DECAL } : base
    chartRef.current?.setOption(next, notMerge)
  }, [option, notMerge, decals])

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ height, width: '100%' }}
      data-testid={rest['data-testid']}
    />
  )
}

export default EChart
